// Component: VideoTemplateForm
// Purpose: Admin form to create/edit a reusable video template. Fetches upload
// URL, supports Canva link, video upload, and optional thumbnail, then submits
// FormData to parent handler.
import React, { useEffect, useState } from 'react';
import { getVideoTemplateUploadUrl } from '../../../api/content/videoTemplatesApi';
import { VIDEO_TEMPLATE_FORMAT_LIST } from '../../../data/tags/tagEnums';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import VideoUploader from '../createTemplates/VideoUploader';
import ThumbnailUploader from '../stockVideos/ThumbnailUploader';

const VideoTemplateForm = ({ onSubmit, isSubmitting, initialData, formData: externalFormData, submitButtonText = 'Create Video Template' }) => {
  const [formData, setFormData] = useState({
    tags: [],
    canvaLink: '',
    searchableText: '',
    videoFile: null,
    videoUploadUrl: '',
    videoUploaded: false,
    permanentVideoUrl: '',
    thumbnailFile: null,
    ...initialData,
    ...externalFormData
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoadingUploadUrl, setIsLoadingUploadUrl] = useState(false);
  const [uploadUrlError, setUploadUrlError] = useState('');
  
  // For URL input values
  const [urlInputValues, setUrlInputValues] = useState({
    canvaLink: ''
  });

  // Fetch video upload URL on component mount
  useEffect(() => {
    if (!formData.videoUploadUrl) {
      fetchVideoUploadUrl();
    }
  }, []);

  // Function to fetch video upload URL from the API
  const fetchVideoUploadUrl = async () => {
    setIsLoadingUploadUrl(true);
    setUploadUrlError('');
    
    try {
      const response = await getVideoTemplateUploadUrl();
      
      if (response && response.uploadUrl && response.permanentUploadPath) {
        setFormData(prev => ({
          ...prev,
          videoUploadUrl: response.uploadUrl,
          permanentUploadPath: response.permanentUploadPath
        }));
        
        console.log('Video template upload URL obtained successfully');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error fetching video template upload URL:', error);
      setUploadUrlError('Failed to get video upload URL. Please try again.');
    } finally {
      setIsLoadingUploadUrl(false);
    }
  };

  // Effect to apply initialData if provided
  useEffect(() => {
    if (initialData) {
      console.log('VideoTemplateForm initialData received:', initialData);
      
      setFormData(prev => ({
        ...prev,
        tags: initialData.tags || [],
        canvaLink: initialData.canvaLink || '',
        searchableText: initialData.searchableText || '',
        permanentVideoUrl: initialData.videoUrl || '',
        thumbnailFile: initialData.thumbnailFile || null
      }));

      // Set URL input values for display (removing protocol and www prefix)
      setUrlInputValues({
        canvaLink: initialData.canvaLink ? initialData.canvaLink.replace(/^(https?:\/\/)?(www\.)?/, '') : ''
      });
    }
  }, [initialData]);

  // Effect to apply external form data (for AI generation)
  useEffect(() => {
    if (externalFormData) {
      console.log('VideoTemplateForm externalFormData received:', externalFormData);
      
      setFormData(prev => ({
        ...prev,
        ...externalFormData
      }));

      // Update URL input values if needed
      if (externalFormData.canvaLink) {
        setUrlInputValues(prev => ({
          ...prev,
          canvaLink: externalFormData.canvaLink.replace(/^(https?:\/\/)?(www\.)?/, '')
        }));
      }
    }
  }, [externalFormData]);

  // Handle changes to form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // For regular form fields
    if (name !== 'canvaLink') {
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    
    // Handle canvaLink with proper URL cleaning (same as TemplateForm)
    handleUrlChange(name, value);
  };

  const handleUrlChange = (name, value) => {
    // Process URL: remove any protocol and www prefix (handles duplicate https://)
    const processedValue = value.replace(/^(https?:\/\/)?(www\.)?/, '');
    
    // Update the input value state (without protocol)
    setUrlInputValues(prev => ({
      ...prev,
      [name]: processedValue,
    }));
    
    // Update form data with https:// prefix
    setFormData(prev => ({
      ...prev,
      [name]: processedValue ? `https://${processedValue}` : '',
    }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => {
      const isSelected = prev.tags.includes(tag);
      const newTags = isSelected
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag];
      
      return { ...prev, tags: newTags };
    });
    
    setTouched(prev => ({ ...prev, tags: true }));
    
    if (errors.tags) {
      setErrors(prev => ({ ...prev, tags: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.tags.length === 0) {
      newErrors.tags = 'At least one format tag is required';
    }

    // Check for video: either existing video (edit mode) or new video uploaded/selected
    const hasExistingVideo = initialData?.videoUrl;
    const hasNewVideo = formData.videoUploaded || formData.videoFile;
    
    if (!hasExistingVideo && !hasNewVideo) {
      newErrors.videoFile = 'Video upload is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormComplete = () => {
    // Check for valid video: either existing or new
    const hasExistingVideo = initialData?.videoUrl;
    const hasNewVideo = formData.videoUploaded || formData.videoFile;
    const hasValidVideo = hasExistingVideo || hasNewVideo;
    
    return formData.tags.length > 0 && hasValidVideo;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      console.log('Form validation failed:', errors);
      return;
    }

    // Create FormData object for submission
    const formDataObj = new FormData();
    
    // Add basic fields
    formDataObj.append('tags', formData.tags.join(','));
    
    // Handle keywords and searchable text
    let searchableText = '';
    if (formData.searchableText) {
      searchableText = formData.searchableText;
    } else {
      searchableText = formData.tags.join(' ');
    }
    formDataObj.append('searchableText', searchableText);
    
    // Add Canva link if provided
    if (formData.canvaLink) {
      formDataObj.append('canvaLink', formData.canvaLink);
    }

    // Add video URL - handle both new uploads and existing videos
    let videoUrlToSubmit = null;
    
    if (formData.permanentVideoUrl) {
      // New video was uploaded and processed
      console.log('[VideoTemplateForm] Using permanentVideoUrl for submission:', formData.permanentVideoUrl);
      videoUrlToSubmit = formData.permanentVideoUrl;
    } else if (formData.permanentUploadPath) {
      // Fallback for new video uploads
      console.log('[VideoTemplateForm] Using permanentUploadPath as fallback:', formData.permanentUploadPath);
      videoUrlToSubmit = formData.permanentUploadPath;
    } else if (initialData?.videoUrl && !formData.videoFile) {
      // Edit mode: no new video selected, use existing video URL
      console.log('[VideoTemplateForm] Using existing video URL for edit mode:', initialData.videoUrl);
      videoUrlToSubmit = initialData.videoUrl;
    } else if (formData.videoFile) {
      // New video file selected but not yet uploaded (shouldn't happen in normal flow)
      console.log('[VideoTemplateForm] No permanent URL found but video file exists. Using placeholder.');
      videoUrlToSubmit = formData.permanentUploadPath || `gs://placeholder-for-video-template-${Date.now()}`;
    }
    
    if (!videoUrlToSubmit) {
      console.error('[VideoTemplateForm] No video URL available for submission');
      setErrors(prev => ({ ...prev, videoFile: 'A video is required. Please upload a video or ensure the existing video is available.' }));
      return;
    }
    
    formDataObj.append('videoUrl', videoUrlToSubmit);
    
    // Add thumbnail if provided
    if (formData.thumbnailFile) {
      formDataObj.append('thumbnailImage', formData.thumbnailFile);
    }

    console.log('[VideoTemplateForm] Submitting form data:', {
      tags: formData.tags,
      canvaLink: formData.canvaLink,
      videoUrl: videoUrlToSubmit,
      hasThumbnail: !!formData.thumbnailFile
    });
    
    onSubmit(formDataObj);
  };

  // Handle video file selection
  const handleVideoSelected = (file) => {
    console.log('[VideoTemplateForm] Video file selected:', file.name);
    setFormData(prev => ({
      ...prev,
      videoFile: file,
      videoUploaded: false,
      permanentVideoUrl: ''
    }));
    
    if (errors.videoFile) {
      setErrors(prev => ({ ...prev, videoFile: '' }));
    }
  };

  // Handle video upload completion
  const handleVideoUploaded = (videoData) => {
    console.log('[VideoTemplateForm] Video uploaded successfully:', videoData);
    setFormData(prev => ({
      ...prev,
      videoUploaded: true,
      permanentVideoUrl: videoData.permanentUrl || videoData.videoUrl || prev.permanentUploadPath,
      videoFile: prev.videoFile // Keep the file reference
    }));
    
    if (errors.videoFile) {
      setErrors(prev => ({ ...prev, videoFile: '' }));
    }
  };

  // Handle thumbnail change
  const handleThumbnailChange = (file) => {
    console.log('[VideoTemplateForm] Thumbnail file selected:', file ? file.name : 'removed');
    
    if (file === null) {
      // Remove thumbnail
      setFormData(prev => ({ ...prev, thumbnailFile: null }));
      if (errors.thumbnailFile) {
        setErrors(prev => ({ ...prev, thumbnailFile: '' }));
      }
    } else if (file && file.isExistingThumbnail) {
      // Handle existing thumbnail (for edit mode)
      // In this case, we don't need to do anything as the form will keep the existing thumbnail
      console.log('[VideoTemplateForm] Using existing thumbnail:', file.name);
    } else {
      // New file selected (either uploaded or auto-generated)
      setFormData(prev => ({ ...prev, thumbnailFile: file }));
      if (errors.thumbnailFile) {
        setErrors(prev => ({ ...prev, thumbnailFile: '' }));
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Upload URL Error */}
      {uploadUrlError && (
        <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Upload URL Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {uploadUrlError}
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={fetchVideoUploadUrl}
                  className="text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Canva Link Field */}
      <div>
        <label htmlFor="canvaLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Canva Template Link
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 sm:text-sm">https://</span>
          </div>
          <input
            type="text"
            id="canvaLink"
            name="canvaLink"
            value={urlInputValues.canvaLink}
            onChange={handleChange}
            placeholder="www.canva.com/design/your-template-id"
            className="w-full pl-16 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
          />
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Optional: Link to the Canva template for easy editing
        </p>
      </div>

      {/* Search Terms */}
      <div>
        <label htmlFor="searchableText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Search Terms (Optional)
        </label>
        <textarea
          id="searchableText"
          name="searchableText"
          rows={3}
          value={formData.searchableText}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
          placeholder="Add comma-separated search terms to help users find this template..."
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add search terms that describe the template content, style, or use cases. This will make your template easily discoverable. Tags are automatically included.
        </p>
      </div>

      {/* Format Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Video Format *
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {VIDEO_TEMPLATE_FORMAT_LIST.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => handleTagToggle(tag)}
              className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors duration-200 ${
                formData.tags.includes(tag)
                  ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
        {errors.tags && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.tags}</p>
        )}
      </div>

      {/* Video Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Video Template *
        </label>
        {isLoadingUploadUrl ? (
          <div className="flex items-center justify-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <div className="text-center">
              <ButtonSpinner size="sm" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Preparing video upload...
              </p>
            </div>
          </div>
        ) : formData.videoUploadUrl ? (
          <VideoUploader
            uploadUrl={formData.videoUploadUrl}
            onVideoSelected={handleVideoSelected}
            onVideoUploaded={handleVideoUploaded}
            existingVideoUrl={initialData?.videoUrl}
            acceptedTypes=".mp4,.mov,.avi,.wmv,.flv,.webm"
            maxSizeMessage="Maximum file size: 500MB"
          />
        ) : null}
        {errors.videoFile && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.videoFile}</p>
        )}
      </div>

      {/* Thumbnail Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Thumbnail Image
        </label>
        <ThumbnailUploader
          onThumbnailChange={handleThumbnailChange}
          error={errors.thumbnailFile}
          existingThumbnail={initialData?.thumbnailUrl ? { url: initialData.thumbnailUrl, name: 'Existing Thumbnail' } : null}
          videoFile={formData.videoFile}
          videoUrl={formData.videoUploaded ? null : (initialData?.videoUrl || formData.videoUploadUrl)}
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {formData.videoFile || (initialData?.videoUrl || formData.videoUploadUrl) ? 
            "💡 You can auto-generate a thumbnail from your video or upload a custom image. If no thumbnail is provided, the system can generate one from the first frame of your video." :
            "Upload a custom thumbnail image. Once you upload a video, you'll also have the option to auto-generate a thumbnail from the video's first frame."
          }
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-3 pt-6">
        <button
          type="submit"
          disabled={!isFormComplete() || isSubmitting}
          className={`px-6 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
            !isFormComplete() || isSubmitting
              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 text-white'
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <ButtonSpinner size="sm" />
              <span className="ml-2">Creating...</span>
            </div>
          ) : (
            submitButtonText
          )}
        </button>
      </div>
    </form>
  );
};

export default VideoTemplateForm; 